import {AddressType} from "@twigsby/client-data/lib/Types/AddressTypes";
import {SendsAddressDetailType} from "@twigsby/client-data/lib/Types/StandaloneSendsTypes";
import {
    AlertBanner,
    ButtonSubmit,
    ColourSolid,
    FlexAlign,
    FlexGap,
    FlexJustify,
    StackVertical
} from "@twigsby/frontend-framework";
import React, {useCallback, useEffect, useState} from "react";
import {FieldValues, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {FileControl} from "../SendMail/Controls/FileControl";
import {useSendMail} from "../../Providers/SendMailProvider";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPaperPlane} from "@fortawesome/pro-solid-svg-icons/faPaperPlane";
import {faTriangleExclamation} from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import {useParams} from "react-router-dom";
import {
    ipmQueryClient,
    sendMail,
    updateSendsPageAddress,
    addUserFile,
    useUpdateSendsDetail, UserFileCategory
} from "@twigsby/client-data";
import {RestrictedAddressControl} from "../SendsUser/Controls/RestrictedAddressControl";
import {RestrictedPostageControl} from "../SendsUser/Controls/RestrictedPostageControl";
import SendMailProcessingModal from "../SendMail/Shared/Modals/SendMailProcessingModal";

export type SendDataProcess = {
    form: any;
    address?: string;
    postage?: string;

    sendProcessOpen?: boolean;

    uploadFileActive?: boolean;
    sendDataActive?: boolean;

    postingData?: boolean;
    sendComplete?: boolean;

    mailUuid?: string;
}

const StandaloneSendForm = () => {
    const { pageId } = useParams();

    const {
        address,
        postage,
        reset,
        sendPageData,
        sendPageLoading,
        sendPageError,
        sendPageID,
        UpdatePageId,
        UpdateAddress,
        ResetSendMail
    } = useSendMail();

    const formMethods = useForm({
        defaultValues: {
            address: -1,
            postage: -1,
            file: null,
        }
    });

    const [ sendError, setSendError ] = useState<string | null>(null);

    const [ sendProcessData, setSendProcessData ] = useState<SendDataProcess>({
        form: null,
        address: address?.value,
        postage: postage?.value,

        sendProcessOpen: false,

        uploadFileActive: false,
        sendDataActive: false,

        postingData: false,

        sendComplete: false
    });

    const {
        handleSubmit,
        reset: formReset,
        formState: { isValid },
    } = formMethods;

    const sendsTemplates = useUpdateSendsDetail();

    const HandleProcessStart = (data: FieldValues) => {
        setSendError(null);
        setSendProcessData({
            ...sendProcessData,
            form: {...data, sendsPageId: sendPageID},
            sendProcessOpen: true,
            address: address?.value,
            postage: postage?.value
        });
    }

    const HandleUpload = () => {
        setSendProcessData({...sendProcessData, uploadFileActive: true, postingData: true });

        addUserFile(sendProcessData.form.file[0], UserFileCategory.MailSends).then((r) => {
            setSendProcessData({...sendProcessData, uploadFileActive: false, sendDataActive: true, postingData: true });
            HandleSend(r.fileUuid);
        })
            .catch((e) => {
                setSendError(e.toString());
                setSendProcessData({...sendProcessData, sendProcessOpen: false, uploadFileActive: false, sendDataActive: false, postingData: false });
            });
    }

    const HandleSend = (fileUuid: string) => {
        sendMail({
            fileUuid: fileUuid,
            notes: sendProcessData.form.notes,
            sendsPageId: sendProcessData.form.sendsPageId,
            addressId: sendProcessData.form.address,
            postageOptionId: sendProcessData.form.postage,
        }).then((s) => {
            setSendProcessData({ ...sendProcessData, sendDataActive: false, sendComplete: true, postingData: true, mailUuid: s.mailUuid });
            ipmQueryClient.invalidateQueries(['allSendsHistory']).then(() => null);
        })
            .catch((e) => {
                setSendError(e.toString());
                setSendProcessData({...sendProcessData, sendProcessOpen: false, uploadFileActive: false, sendDataActive: false, postingData: false });
            });
    }

    const HandleNewAddress = useCallback(async (singleAddress: AddressType) => {
        if(sendPageData) {
            const newPostageArray: Array<number> = [];
            sendPageData.addresses[0].postageOptions.forEach((postage: any) => newPostageArray.push(postage.id));

            const patchAddresses = [{ id: singleAddress.addressId, postageOptions: newPostageArray }];
            sendPageData.addresses.forEach((address: any) => patchAddresses.push({ id: address.id, postageOptions: address?.postageOptions.map((postage: any) => postage.id) }));

            const newAddressUiData = [
                ...sendPageData.addresses,
                {
                    value: `${singleAddress.name}, ${singleAddress.addressLine1}, ${singleAddress.addressLine2}, ${singleAddress.postCode}, ${singleAddress.countryCode}`,
                    id: singleAddress.addressId,
                    postageOptions: [...sendPageData.addresses[0].postageOptions]
                }];

            await ForwardAddressChangeData(patchAddresses, newAddressUiData, singleAddress.addressId, 1);
        }

    }, [sendPageData]);

    const HandleNewBulkAddresses = useCallback(async (bulkAddresses: Array<AddressType>) => {
        if(sendPageData) {
            const newPostageArray: Array<number> = [];
            sendPageData.addresses[0].postageOptions.forEach((postage: any) => newPostageArray.push(postage.id));

            const patchAddresses: Array<any> = [];
            sendPageData.addresses.forEach((address: any) => patchAddresses.push({
                id: address.id,
                postageOptions: address?.postageOptions.map((postage: any) => postage.id)
            }));

            const newBulkAddressesUiData: Array<SendsAddressDetailType> = [];

            bulkAddresses.forEach((item: AddressType) => {
                patchAddresses.push({
                    id: item.addressId,
                    postageOptions: newPostageArray
                });

                newBulkAddressesUiData.push({
                    id: item.addressId,
                    value: item.addressString,
                    postageOptions: [...sendPageData.addresses[0].postageOptions]
                })
            });

            const newAddressUiData = [
                ...sendPageData.addresses,
                ...newBulkAddressesUiData
            ];

            if (bulkAddresses.length !== 0) {
                await ForwardAddressChangeData(patchAddresses, newAddressUiData, bulkAddresses[0].addressId, bulkAddresses.length);
            }
        }
    }, [sendPageData]);

    const ForwardAddressChangeData = async (patchData: any, uiData: any, nextValue: number, addressCount: number) => {
        await updateSendsPageAddress(sendPageData?.id as number, patchData)
            .then((resp: any) => {

                sendsTemplates.mutateAsync({
                    newData: {...sendPageData, addresses: uiData }
                }).then(() => {
                    UpdateAddress(nextValue, true);
                });

            })
            .catch((e: any) => console.error(e))
    }

    const ResetSend = () => {
        formReset();
        ResetSendMail();
        setSendProcessData({
            address: address?.value,
            postage: postage?.value,
            form: null,

            sendProcessOpen: false,

            uploadFileActive: false,
            sendDataActive: false,

            postingData: false,
            sendComplete: false
        });
    }

    useEffect(() => {
        if(pageId) {
            UpdatePageId(parseInt(pageId as string));
            ResetSend();
        }
    }, [pageId]);

    return (
        <FormProvider {...formMethods}>
            <div className={'sticky'}>
                {sendPageLoading ? (
                    <div className={'content-loader content-fader'}>
                        <div style={{ height: '53.5em' }}/>
                    </div>
                ) : sendPageError ? (
                    <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'p-xxl text-center'}>
                        <FontAwesomeIcon icon={faTriangleExclamation} size={"3x"} />
                        <h6 className={'sub-heading mt-sm mb-xs'}>Error Loading Page: {pageId}</h6>
                        <p>The requested page could not be found, or is no longer available. If you believe this to be incorrect, please contact the account owner.</p>
                    </StackVertical>
                ) : (
                    <>
                        {sendPageData?.isActive && (
                            <div className={'card'}>
                                <div className={'card-body'}>
                                    <form onSubmit={handleSubmit((data, e) => HandleProcessStart(data))}>
                                        <fieldset>
                                            <h4 className={'mb-0'}>{sendPageData.title ? sendPageData.title : 'Title'}</h4>
                                            <p>{sendPageData.description ? sendPageData.description : ''}</p>

                                            <hr />

                                            <div className={'flex-column flex-gap-md'}>
                                                <StackVertical gap={FlexGap.ExtraSmall}>
                                                    <RestrictedAddressControl
                                                        allowCreateAddress={sendPageData.allowCreateAddress}
                                                        addAddressCallback={(address) => HandleNewAddress(address)}
                                                        addBulkAddressCallback={(addresses) => HandleNewBulkAddresses(addresses)}
                                                    />
                                                </StackVertical>

                                                <RestrictedPostageControl />

                                                <FileControl name={'file'} />

                                                {sendError && (
                                                    <AlertBanner title={sendError} colourMod={ColourSolid.Alert} />
                                                )}

                                                <ButtonSubmit
                                                    loading={sendProcessData.sendProcessOpen}
                                                    title={'Send'}
                                                    colour={ColourSolid.Primary}
                                                    fullWidth={true}
                                                    disabled={!isValid}
                                                />

                                            </div>
                                        </fieldset>
                                    </form>

                                </div>
                            </div>
                        )}

                        {!sendPageData?.isActive && (
                            <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'m-xxl p-xxl text-center'}>
                                <FontAwesomeIcon icon={faPaperPlane} size={"3x"} />
                                <h6 className={'sub-heading mt-sm mb-xs'}>Page No Longer Active</h6>
                                <p>If you believe this is an error, please contact your sends administrator?</p>
                            </StackVertical>
                        )}

                    </>
                )}

                <SendMailProcessingModal
                    isOpen={sendProcessData.sendProcessOpen as boolean}
                    sendData={sendProcessData}
                    actionedCallback={() => HandleUpload() }
                    closeCallback={() => sendProcessData.postingData ? ResetSend() : setSendProcessData({...sendProcessData, sendProcessOpen: false }) }
                    lockOpen={sendProcessData.postingData}
                />
            </div>

        </FormProvider>
    )
}

export default StandaloneSendForm
